import { gql, useMutation, useQuery } from '@apollo/client'
import { useCallback, useMemo } from 'react'

import {
  BlockUser,
  BlockUserMutation,
  BlockUserMutationVariables,
  CommentBoxWithoutRepliesFragmentDoc,
  CreateArtistThreadComment,
  CreateArtistThreadCommentMutation,
  CreateArtistThreadCommentMutationVariables,
  CreateItemComment,
  CreateItemCommentMutation,
  CreateItemCommentMutationVariables,
  CreateMissionComment,
  CreateMissionCommentMutation,
  CreateMissionCommentMutationVariables,
  DeleteArtistThreadComment,
  DeleteArtistThreadCommentMutation,
  DeleteArtistThreadCommentMutationVariables,
  DeleteItemComment,
  DeleteItemCommentMutation,
  DeleteItemCommentMutationVariables,
  DeleteMissionComment,
  DeleteMissionCommentMutation,
  DeleteMissionCommentMutationVariables,
  ItemReplyDetailQuery,
  ItemReplyDetailQueryVariables,
  LikeItemComment,
  LikeItemCommentMutation,
  LikeItemCommentMutationVariables,
  LikeMissionComment,
  LikeMissionCommentMutation,
  LikeMissionCommentMutationVariables,
  LikeThreadComment,
  LikeThreadCommentMutation,
  LikeThreadCommentMutationVariables,
  MissionReplyDetailQuery,
  MissionReplyDetailQueryVariables,
  ThreadReplyDetailQuery,
  ThreadReplyDetailQueryVariables,
  UnlikeItemComment,
  UnlikeItemCommentMutation,
  UnlikeItemCommentMutationVariables,
  UnlikeMissionComment,
  UnlikeMissionCommentMutation,
  UnlikeMissionCommentMutationVariables,
  UnlikeThreadComment,
  UnlikeThreadCommentMutation,
  UnlikeThreadCommentMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { openReportForm } from '@/core/googleForm'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { analytics } = useAnalytics()
  const { router } = useRouter<{
    id: string
    commentId: string
    type: 'missions' | 'threads' | 'items'
  }>()

  const {
    data: threadData,
    loading: threadLoading,
    fetchMore: threadFetchMore,
    refetch: threadRefetch,
  } = useQuery<ThreadReplyDetailQuery, ThreadReplyDetailQueryVariables>(
    THREAD_QUERY,
    {
      variables: {
        id: router.query.id,
        commentId: router.query.commentId,
        pageInput: { limit: 10 },
      },
      skip: router.query.type !== 'threads',
      returnPartialData: true,
      notifyOnNetworkStatusChange: true,
    }
  )

  const {
    data: missionData,
    loading: missionLoading,
    fetchMore: missionFetchMore,
    refetch: missionRefetch,
  } = useQuery<MissionReplyDetailQuery, MissionReplyDetailQueryVariables>(
    MISSION_QUERY,
    {
      variables: {
        id: router.query.id,
        commentId: router.query.commentId,
        pageInput: { limit: 10 },
      },
      skip: router.query.type !== 'missions',
      returnPartialData: true,
      notifyOnNetworkStatusChange: true,
    }
  )

  const {
    data: itemData,
    loading: itemLoading,
    fetchMore: itemFetchMore,
    refetch: itemRefetch,
  } = useQuery<ItemReplyDetailQuery, ItemReplyDetailQueryVariables>(
    ITEM_QUERY,
    {
      variables: {
        id: router.query.id,
        commentId: router.query.commentId,
        pageInput: { limit: 10 },
      },
      skip: router.query.type !== 'items',
      returnPartialData: true,
      notifyOnNetworkStatusChange: true,
    }
  )

  const [likeThreadComment] = useMutation<
    LikeThreadCommentMutation,
    LikeThreadCommentMutationVariables
  >(LikeThreadComment)

  const [likeMissionComment] = useMutation<
    LikeMissionCommentMutation,
    LikeMissionCommentMutationVariables
  >(LikeMissionComment)

  const [likeItemComment] = useMutation<
    LikeItemCommentMutation,
    LikeItemCommentMutationVariables
  >(LikeItemComment)

  const [unlikeThreadComment] = useMutation<
    UnlikeThreadCommentMutation,
    UnlikeThreadCommentMutationVariables
  >(UnlikeThreadComment)

  const [unlikeMissionComment] = useMutation<
    UnlikeMissionCommentMutation,
    UnlikeMissionCommentMutationVariables
  >(UnlikeMissionComment)

  const [unlikeItemComment] = useMutation<
    UnlikeItemCommentMutation,
    UnlikeItemCommentMutationVariables
  >(UnlikeItemComment)

  const [createArtistThreadComment] = useMutation<
    CreateArtistThreadCommentMutation,
    CreateArtistThreadCommentMutationVariables
  >(CreateArtistThreadComment)

  const [createMissionComment] = useMutation<
    CreateMissionCommentMutation,
    CreateMissionCommentMutationVariables
  >(CreateMissionComment)

  const [createItemComment] = useMutation<
    CreateItemCommentMutation,
    CreateItemCommentMutationVariables
  >(CreateItemComment)

  const [deleteArtistThreadComment] = useMutation<
    DeleteArtistThreadCommentMutation,
    DeleteArtistThreadCommentMutationVariables
  >(DeleteArtistThreadComment)

  const [deleteMissionComment] = useMutation<
    DeleteMissionCommentMutation,
    DeleteMissionCommentMutationVariables
  >(DeleteMissionComment)

  const [deleteItemComment] = useMutation<
    DeleteItemCommentMutation,
    DeleteItemCommentMutationVariables
  >(DeleteItemComment)

  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BlockUser)

  const getRepliesAndComment = useCallback(
    <T extends { replies?: unknown }>(
      data: T | undefined | null
    ): {
      replies: Pick<T, 'replies'> | null | unknown
      comment: Omit<T, 'replies'> | null
    } => {
      if (!data) {
        return { replies: null, comment: null }
      }
      const { replies, ...comment } = data
      return { replies, comment }
    },
    []
  )

  const state: State = useMemo(() => {
    switch (router.query.type) {
      case 'threads':
        return {
          rootCollectionId: threadData?.artistThreadChannel?.id,
          artistId: threadData?.artistThreadChannel?.artistId ?? '',
          user: threadData?.user ?? null,
          hasNextPage:
            !!threadData?.artistThreadChannelComment?.replies?.pageInfo
              ?.hasNextPage,
          loading: threadLoading,
          ...getRepliesAndComment(threadData?.artistThreadChannelComment),
        }
      case 'missions':
        return {
          rootCollectionId: missionData?.mission?.id,
          artistId: missionData?.mission?.artistId ?? '',
          user: missionData?.user ?? null,
          hasNextPage:
            !!missionData?.missionComment?.replies?.pageInfo?.hasNextPage,
          loading: missionLoading,
          ...getRepliesAndComment(missionData?.missionComment),
        }
      default:
        return {
          rootCollectionId: itemData?.item?.id,
          artistId: itemData?.item?.issuedArtist?.id ?? '',
          user: itemData?.user ?? null,
          hasNextPage: !!itemData?.itemComment?.replies?.pageInfo?.hasNextPage,
          loading: itemLoading,
          ...getRepliesAndComment(itemData?.itemComment),
        }
    }
  }, [
    getRepliesAndComment,
    itemData?.item?.id,
    itemData?.item?.issuedArtist?.id,
    itemData?.itemComment,
    itemData?.user,
    itemLoading,
    missionData?.mission?.artistId,
    missionData?.mission?.id,
    missionData?.missionComment,
    missionData?.user,
    missionLoading,
    router.query.type,
    threadData?.artistThreadChannel?.artistId,
    threadData?.artistThreadChannel?.id,
    threadData?.artistThreadChannelComment,
    threadData?.user,
    threadLoading,
  ])

  return {
    state,
    actions: {
      async onFetchMore() {
        switch (router.query.type) {
          case 'threads':
            await threadFetchMore({
              variables: {
                id: router.query.id,
                commentId: router.query.commentId,
                pageInput: {
                  cursor:
                    threadData?.artistThreadChannelComment?.replies?.pageInfo
                      .endCursor || null,
                },
              },
            })
            break
          case 'missions':
            await missionFetchMore({
              variables: {
                id: router.query.id,
                commentId: router.query.commentId,
                pageInput: {
                  cursor:
                    missionData?.missionComment?.replies?.pageInfo.endCursor ||
                    null,
                },
              },
            })
            break
          case 'items':
            await itemFetchMore({
              variables: {
                id: router.query.id,
                commentId: router.query.commentId,
                pageInput: {
                  cursor:
                    itemData?.itemComment?.replies?.pageInfo.endCursor || null,
                },
              },
            })
            break
        }
      },
      async onReply(inputs, artistId) {
        const variables = {
          id: router.query.id,
          inputs: {
            artistId,
            ...inputs,
            replyToId: router.query?.commentId,
            replyToAccountId: state?.comment?.commenter?.accountId,
          },
        }
        switch (router.query.type) {
          case 'threads':
            await createArtistThreadComment({ variables })
            break
          case 'missions':
            await createMissionComment({ variables })
            break
          case 'items':
            await createItemComment({ variables })
            break
        }
        analytics?.track('click_sendComment', {
          applicationKey: config.applicationKey,
          threadId: router.query.id,
          artistId,
          // remove 's' character
          name: router.query.type.slice(0, -1),
        })
      },
      async onLikeOrUnlikeComment({ isLike, commentId, likeCount, id }) {
        const variables = { inputs: { id: id ?? '', commentId } }
        const optimisticLike = {
          __typename: 'Comment',
          id: commentId,
          isLike: true,
          likeCount: ++likeCount,
        }
        const optimisticUnlike = {
          __typename: 'Comment',
          id: commentId,
          isLike: true,
          likeCount: ++likeCount,
        }
        switch (router.query.type) {
          case 'threads':
            isLike
              ? await unlikeThreadComment({
                  variables,
                  optimisticResponse: () => ({
                    unlikeArtistThreadChannelComment: optimisticUnlike,
                  }),
                })
              : await likeThreadComment({
                  variables,
                  optimisticResponse: () => ({
                    likeArtistThreadChannelComment: optimisticLike,
                  }),
                })
            break
          case 'missions':
            isLike
              ? await unlikeMissionComment({
                  variables,
                  optimisticResponse: () => ({
                    unlikeMissionComment: optimisticUnlike,
                  }),
                })
              : await likeMissionComment({
                  variables,
                  optimisticResponse: () => ({
                    likeMissionComment: optimisticLike,
                  }),
                })
            break
          case 'items':
            isLike
              ? await unlikeItemComment({
                  variables,
                  optimisticResponse: () => ({
                    unlikeItemComment: optimisticUnlike,
                  }),
                })
              : await likeItemComment({
                  variables,
                  optimisticResponse: () => ({
                    likeItemComment: optimisticLike,
                  }),
                })
            break
        }
      },
      async onDeleteComment(commentId) {
        const variables = {
          id: router.query.id,
          commentId,
        }
        switch (router.query.type) {
          case 'threads':
            await deleteArtistThreadComment({ variables })
            break
          case 'missions':
            await deleteMissionComment({ variables })
            break
          case 'items':
            await deleteItemComment({ variables })
            break
        }
        analytics?.track('click_deleteComment', {
          applicationKey: config.applicationKey,
          threadId: router.query.id,
          artistId: state?.artistId,
          // remove 's' character
          name: router.query.type.slice(0, -1),
        })
      },
      async onBlockUser(userId) {
        await blockUser({ variables: { id: userId } })
        switch (router.query.type) {
          case 'threads':
            await threadRefetch()
            break
          case 'missions':
            await missionRefetch()
            break
          case 'items':
            await itemRefetch()
            break
        }
      },
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: state?.user?.email ?? '',
          // remove 's' character
          contentType: router.query.type.slice(0, -1),
          contentId,
          userId,
          accountId,
        })
      },
    },
  }
})

const THREAD_QUERY = gql`
  query ThreadReplyDetail(
    $id: ID!
    $commentId: ID!
    $pageInput: PageInputDate
  ) {
    artistThreadChannel(id: $id) {
      id
      artistId
    }
    artistThreadChannelComment(id: $id, commentId: $commentId) {
      ... on Comment {
        ...CommentBoxWithoutReplies
        replies(pageInput: $pageInput) {
          edges {
            node {
              ... on Comment {
                ...CommentBoxWithoutReplies
                replies {
                  edges {
                    node {
                      ... on Comment {
                        ...CommentBoxWithoutReplies
                      }
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      email
      avatar {
        uri
      }
    }
  }
  ${CommentBoxWithoutRepliesFragmentDoc}
`

const MISSION_QUERY = gql`
  query MissionReplyDetail(
    $id: ID!
    $commentId: ID!
    $pageInput: PageInputDate
  ) {
    mission(id: $id) {
      id
      artistId
    }
    missionComment(id: $id, commentId: $commentId) {
      ... on Comment {
        ...CommentBoxWithoutReplies
        replies(pageInput: $pageInput) {
          edges {
            node {
              ... on Comment {
                ...CommentBoxWithoutReplies
                replies {
                  edges {
                    node {
                      ... on Comment {
                        ...CommentBoxWithoutReplies
                      }
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      email
      avatar {
        uri
      }
    }
  }
  ${CommentBoxWithoutRepliesFragmentDoc}
`

const ITEM_QUERY = gql`
  query ItemReplyDetail($id: ID!, $commentId: ID!, $pageInput: PageInputDate) {
    item(id: $id) {
      id
      issuedArtist {
        id
      }
    }
    itemComment(id: $id, commentId: $commentId) {
      ... on Comment {
        ...CommentBoxWithoutReplies
        replies(pageInput: $pageInput) {
          edges {
            node {
              ... on Comment {
                ...CommentBoxWithoutReplies
                replies {
                  edges {
                    node {
                      ... on Comment {
                        ...CommentBoxWithoutReplies
                      }
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      email
      avatar {
        uri
      }
    }
  }
  ${CommentBoxWithoutRepliesFragmentDoc}
`
